import moment from 'moment';
import http from '@/services/http';
import tableHelper from '@/services/tableHelper';
import familyTypesFilters from '@/config/filters/familyTypes';
import specificationsFilters from '@/config/filters/specifications';

const analyses = {
  autocompleteFetch(key, value, contains = true, includeExpired = true) {
    const filter = {
      where: {
        and: [{ [key]: { regexp: contains ? `/${value}/i` : `/^${value}/i` } }],
      },
    };
    if (!includeExpired) {
      filter.where.and.push({
        or: [
          { expirationDate: null },
          { expirationDate: { gt: moment.utc().startOf('day').toDate() } },
        ],
      });
    }
    return http.request({
      method: 'get',
      url: '/analyses',
      params: { filter },
    });
  },
  autocompleteFetchById(id, includeExpired = true) {
    const filter = {
      where: {
        and: [{ id: Number(id) }],
      },
    };
    if (!includeExpired) {
      filter.where.and.push({
        or: [
          { expirationDate: null },
          { expirationDate: { gt: moment.utc().startOf('day').toDate() } },
        ],
      });
    }
    return http.request({
      method: 'get',
      url: '/analyses',
      params: { filter },
    });
  },
  analysesWithoutOperations() {
    return http.request({
      method: 'get',
      url: `/analyses/without-operations`,
    });
  },
  operationsIndex(analysisId, tableFilters) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations`,
      params: { filter: { ...tableFilters } },
    });
  },
  async analysesShow(analysisId) {
    const response = await http.request({
      method: 'get',
      url: '/analyses',
      params: { filter: { where: { id: Number(analysisId) } } },
    });
    return { ...response, data: response.data[0] ?? null };
  },
  operationsInAnalysis(analysisId) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations`,
    });
  },
  operationsCount(analysisId) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations/count`,
    });
  },
  operationsShow(analysisId, operationId) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations/${operationId}`,
    });
  },
  operationsCreate(analysisId, data) {
    return http.request({
      method: 'post',
      url: `/analyses/${analysisId}/operations`,
      data,
    });
  },
  operationsPut(analysisId, operationId, data) {
    return http.request({
      method: 'put',
      url: `/analyses/${analysisId}/operations/${operationId}`,
      data,
    });
  },
  operationsPatch(analysisId, operationId, data) {
    return http.request({
      method: 'patch',
      url: `/analyses/${analysisId}/operations/${operationId}`,
      data,
    });
  },
  operationsDelete(analysisId, operationId) {
    return http.request({
      method: 'delete',
      url: `/analyses/${analysisId}/operations/${operationId}`,
    });
  },
  operationsCopy(analysisId, operations) {
    return http.request({
      method: 'post',
      url: `/analyses/${analysisId}/operation-copies`,
      data: operations,
    });
  },
  defaultLocationsIndex(analysisId, tableFilters) {
    return http.request({
      method: 'get',
      url: '/analysis-locations',
      params: {
        filter: {
          ...tableFilters,
          where: { analysisId },
          include: ['location'],
        },
      },
    });
  },
  defaultLocationsCount(analysisId) {
    return http.request({
      method: 'get',
      url: '/analysis-locations/count',
      params: { filter: { where: { analysisId } } },
    });
  },
  defaultLocationsCreate(data) {
    return http.request({
      method: 'post',
      url: '/analysis-locations',
      data,
    });
  },
  defaultLocationsDelete(id) {
    return http.request({
      method: 'delete',
      url: `/analysis-locations/${id}`,
    });
  },
  defaultLocationsPatch(id, data) {
    return http.request({
      method: 'patch',
      url: `/analysis-locations/${id}`,
      data,
    });
  },
  familyTypesIndex(tableFilters, filterStatus, config = {}) {
    const filters = tableHelper.whereFilters(filterStatus, familyTypesFilters);
    return http.request({
      method: 'get',
      url: '/analyses-family-types',
      params: { filter: { ...tableFilters, where: filters } },
      ...config,
    });
  },
  familyTypesCount(filterStatus, config = {}) {
    const where = tableHelper.whereFilters(filterStatus, familyTypesFilters);
    return http.request({
      method: 'get',
      url: '/analyses-family-types/count',
      params: { where },
      ...config,
    });
  },
  familyTypesCreate(data) {
    return http.request({
      method: 'post',
      url: '/analyses-family-types',
      data,
    });
  },
  familyTypesPatch(id, data) {
    return http.request({
      method: 'patch',
      url: `/analyses-family-types/${id}`,
      data,
    });
  },
  familyTypesAll() {
    return http.request({
      method: 'get',
      url: '/analyses-family-types',
      params: { filter: { order: ['familyCode ASC'] } },
    });
  },
  familyCategories() {
    return http.request({
      method: 'get',
      url: '/family-categories',
    });
  },
  familyCategoriesCreate(data) {
    return http.request({
      method: 'post',
      url: '/family-categories',
      data,
    });
  },
  familyCategoriesPatch(code, data) {
    return http.request({
      method: 'patch',
      url: `/family-categories/${code}`,
      data,
    });
  },
  familyCategoriesDelete(code) {
    return http.request({
      method: 'delete',
      url: `/family-categories/${code}`,
    });
  },
  searchFamilies(text) {
    const escaped = text.replace(/[.*+?^${}()|[\]\\/]/g, '\\$&');
    const regexp = `/${escaped}/i`;
    return http.request({
      method: 'get',
      url: '/analysis-families',
      params: {
        filter: {
          where: {
            or: [{ family: { regexp } }, { familyCode: { regexp } }],
          },
          order: ['family ASC'],
          limit: 20,
        },
      },
    });
  },
  getFamiliesAutocomplete(key, value, contains = true, includeExpired = true) {
    const filter = {
      where: {
        and: [{ [key]: { regexp: contains ? `/${value}/i` : `/^${value}/i` } }],
      },
    };
    if (!includeExpired) {
      filter.where.and.push({
        or: [
          { expirationDate: null },
          { expirationDate: { gt: moment.utc().startOf('day').toDate() } },
        ],
      });
    }
    return http.request({
      method: 'get',
      url: '/analysis-families',
      params: { filter },
    });
  },
  specificationsShow(analysisId, operationId) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations/${operationId}/specification`,
    });
  },
  specificationPatch(analysisId, operationId, data, specificationId) {
    return http.request({
      method: 'patch',
      url: `/analyses/${analysisId}/operations/${operationId}/specification/${specificationId}`,
      data,
    });
  },
  specificationCreate(analysisId, operationId, data) {
    return http.request({
      method: 'post',
      url: `/analyses/${analysisId}/operations/${operationId}/specification`,
      data,
    });
  },
  specificationsIndex(analysisId, tableFilters) {
    const filters = tableHelper.whereFilters(
      tableFilters,
      specificationsFilters
    );
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations-with-specification`,
      params: { filter: { where: filters } },
    });
  },
  specificationsCount(analysisId) {
    return http.request({
      method: 'get',
      url: `/analyses/${analysisId}/operations-with-specification/count`,
    });
  },
  specificationsDelete(analysisId, operationId, specificationId) {
    return http.request({
      method: 'delete',
      url: `/analyses/${analysisId}/operations/${operationId}/specification/${specificationId}`,
    });
  },
  validateOperation(data) {
    return http.request({
      method: 'post',
      url: '/analyses/operations/validate',
      data,
    });
  },
  brandsAutocompleteFetch(brand) {
    const filter = {
      where: {
        brand: {
          regexp: `/${brand || ''}/i`,
        },
      },
    };
    return http.request({
      method: 'get',
      url: '/analysis-brands',
      params: { filter },
    });
  },
  methodsAutocompleteFetch(method) {
    const filter = {
      where: {
        method: {
          regexp: `/${method || ''}/i`,
        },
      },
    };
    return http.request({
      method: 'get',
      url: '/analysis-methods',
      params: { filter },
    });
  },
  /**
   * Updates showTemperature, showDilution, showSymbol flags and defaultDilution
   */
  updateReportFlagsPatch(analysisId, data) {
    const ALLOWED = [
      'showTemperature',
      'showSymbol',
      'showDilution',
      'defaultDilution',
    ];
    const payload = Object.fromEntries(
      Object.entries(data).filter(([k]) => ALLOWED.includes(k))
    );
    return http.request({
      method: 'patch',
      url: `/analyses/${analysisId}/report-config`,
      data: payload,
    });
  },
};

export default analyses;
